import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { TAG_COLORS, getTagColorStyles, type TagColor } from "@/modules/tag/colors";
import IconCheck from "../icons/icon-check";
import IconChevronDown from "../icons/icon-chevron-down";

interface TagColorPickerProps {
	value: string | null | undefined;
	onChange: (color: TagColor) => void;
	disabled?: boolean;
	error?: boolean;
}

export default function TagColorPicker({ value, onChange, disabled = false, error = false }: TagColorPickerProps) {
	const [open, setOpen] = useState(false);
	const selectedColor = getTagColorStyles(value);

	const handleSelect = (color: TagColor) => {
		if (disabled) return;
		onChange(color);
		setOpen(false);
	};

	return (
		<DropdownMenu.Root open={disabled ? false : open} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					disabled={disabled}
					onClick={(e) => {
						if (disabled) {
							e.preventDefault();
							e.stopPropagation();
						}
					}}
					className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${error ? "border-red-500 ring-2 ring-red-500 ring-offset-1" : "border-gray-300"} ${open ? "ring-2 ring-brand ring-offset-1" : ""} bg-white hover:border-gray-400`}
					title="Select color"
				>
					<span className={`w-5 h-5 rounded-md shrink-0 ${selectedColor.bg} ${selectedColor.border || ""}`} />
					<span className="text-sm text-gray-700 whitespace-nowrap">{selectedColor.label}</span>
					<IconChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content
					className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50 min-w-[180px] max-h-[300px] overflow-y-auto"
					sideOffset={4}
					align="start"
				>
					<div className="py-1">
						{TAG_COLORS.map((color) => (
							<DropdownMenu.Item
								key={color.value}
								className="outline-none"
								onSelect={(e) => {
									e.preventDefault();
									handleSelect(color.value);
								}}
							>
								<button
									type="button"
									className={`w-full flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 ${value === color.value ? "bg-gray-100" : ""}`}
								>
									<span className={`w-5 h-5 rounded-md shrink-0 ${color.bg} ${color.border || ""}`} />
									<span className="text-sm text-gray-700 flex-1 text-left">{color.label}</span>
									{value === color.value && <IconCheck className="w-4 h-4 text-brand" />}
								</button>
							</DropdownMenu.Item>
						))}
					</div>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}
